import { cn } from "@/lib/cn";
import { shallow, useOthersMapped } from "@liveblocks/react";
import { useEffect, useState } from "react";
import { useCursorsIntersection } from "../hooks/useCursorsIntersection";
import { CollaborationEnabledProvider } from "../providers/CollaborationEnabledProvider";

const MESSAGE_FADE_OUT_MS = 1500;
const BUBBLE_FONT = "500 14px sans-serif";
const BUBBLE_HORIZONTAL_PADDING = 28;
const MIN_TYPED_BUBBLE_WIDTH = 52;
const MAX_BUBBLE_WIDTH = 200;

let measureCanvas: HTMLCanvasElement | null = null;

type UserInfo = {
  color: string;
  name: string;
  theme: "light" | "dark";
};

type CursorMessage = {
  id: string;
  text: string;
  createdAt: number;
  expiresAt: number;
};

type CursorPresence = {
  x: number;
  y: number;
  id: string;
  message?: CursorMessage;
};

type TCursor = {
  color: string;
  x: number;
  y: number;
  name: string;
  theme: string;
  message?: CursorMessage;
};

export const Cursors = ({ id }: { id: string }) => {
  const isIntersecting = useCursorsIntersection({ id });
  if (!isIntersecting) return null;

  return (
    <CollaborationEnabledProvider>
      <Main id={id} />
    </CollaborationEnabledProvider>
  );
};

const Main = ({ id }: { id: string }) => {
  const others = useOthersMapped(
    (other) => ({
      info: other.info as UserInfo,
      cursor: other.presence.cursor as CursorPresence | null,
    }),
    shallow,
  );
  return (
    <>
      {others?.map(([connectionId, { info, cursor }]) => {
        if (!info || !cursor || cursor.id !== id) return null;
        return (
          <Cursor
            key={`cursor-${connectionId}`}
            color={info.color as string}
            x={cursor.x}
            y={cursor.y}
            theme={info.theme as string}
            name={info.name as string}
            message={cursor.message}
          />
        );
      })}
    </>
  );
};

const Cursor = ({ color, x, y, name, theme, message }: TCursor) => {
  return (
    <div
      style={{
        position: "absolute",
        left: `${x}%`,
        top: `${y}%`,
        zIndex: 999,
        transition: "left 60ms linear, top 60ms linear",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M15.55 5.37L0.89 0.04C0.77 0 0.64 -0.01 0.52 0.02C0.4 0.04 0.28 0.11 0.2 0.2C0.11 0.28 0.04 0.4 0.02 0.52C-0.01 0.65 0 0.77 0.04 0.89L5.37 15.56C5.42 15.69 5.5 15.8 5.61 15.88C5.73 15.96 5.86 16 6 16C6.13 16 6.26 15.96 6.37 15.89C6.48 15.81 6.57 15.7 6.62 15.58L9.17 9.18L15.57 6.62C15.7 6.57 15.81 6.49 15.88 6.38C15.96 6.26 16 6.13 16 6C16 5.86 15.96 5.73 15.88 5.62C15.81 5.51 15.7 5.42 15.57 5.37H15.55Z"
          fill={color}
        />
      </svg>

      {message ? (
        <CursorMessageBubble color={color} message={message} theme={theme} />
      ) : (
        <CursorLabel color={color} theme={theme}>
          {name}
        </CursorLabel>
      )}
    </div>
  );
};

const CursorMessageBubble = ({
  color,
  message,
  theme,
}: {
  color: string;
  message: CursorMessage;
  theme: string;
}) => {
  const [visible, setVisible] = useState(() => Date.now() < message.expiresAt);

  useEffect(() => {
    const remainingMs = message.expiresAt - Date.now();

    if (remainingMs <= 0) {
      setVisible(false);
      return;
    }

    setVisible(true);

    const timeout = window.setTimeout(() => setVisible(false), remainingMs);

    return () => window.clearTimeout(timeout);
  }, [message.expiresAt, message.id]);

  return (
    <span
      className={cn(
        "-tw-mt-1 tw-ml-3 tw-box-border tw-block tw-min-h-7 tw-max-w-[200px] tw-whitespace-normal tw-break-words tw-rounded-[14px] tw-px-2.5 tw-py-1 tw-text-left tw-font-sans tw-text-sm tw-font-medium tw-leading-5 tw-text-white tw-shadow-md tw-transition-opacity",
        {
          "tw-text-blue-base": theme === "light",
        },
        visible ? "tw-opacity-100" : "tw-opacity-0",
      )}
      style={{
        backgroundColor: color,
        transitionDuration: `${MESSAGE_FADE_OUT_MS}ms`,
        width: getBubbleWidth(message.text),
      }}
    >
      {message.text}
    </span>
  );
};

const CursorLabel = ({
  children,
  className,
  color,
  theme,
}: {
  children: string;
  className?: string;
  color: string;
  theme: string;
}) => {
  return (
    <span
      className={cn(
        "-tw-mt-1 tw-ml-3 tw-block tw-h-7 tw-max-w-[220px] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-rounded-[100px] tw-px-3 tw-text-center tw-font-sans tw-text-sm tw-font-medium tw-leading-7 tw-text-white tw-shadow-md",
        {
          "tw-text-blue-base": theme === "light",
        },
        className,
      )}
      style={{ backgroundColor: color }}
    >
      {children}
    </span>
  );
};

const getBubbleWidth = (text: string) => {
  const measuredWidth = measureBubbleText(text) + BUBBLE_HORIZONTAL_PADDING;

  return `${Math.min(
    MAX_BUBBLE_WIDTH,
    Math.max(MIN_TYPED_BUBBLE_WIDTH, Math.ceil(measuredWidth)),
  )}px`;
};

const measureBubbleText = (text: string) => {
  if (typeof document === "undefined") return text.length * 7;

  measureCanvas ??= document.createElement("canvas");

  const context = measureCanvas.getContext("2d");
  if (!context) return text.length * 7;

  context.font = BUBBLE_FONT;

  return context.measureText(text).width;
};
